import Link from "next/link";
import { useState } from "react";
function IconScience() {
  return (
    <svg viewBox="0 0 64 64" className="w-12 h-12">
      <defs>
        <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#22d3ee" />
          <stop offset="100%" stopColor="#818cf8" />
        </linearGradient>
      </defs>

      {/* DNA curve */}
      <path
        d="M20 8c8 8 16 8 24 16s0 16-8 24"
        fill="none"
        stroke="url(#g1)"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M44 8c-8 8-16 8-24 16s0 16 8 24"
        fill="none"
        stroke="url(#g1)"
        strokeWidth="3"
        strokeLinecap="round"
      />

      {/* Grid dots */}
      {[16, 32, 48].map((x) =>
        [20, 32, 44].map((y) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="1.5" fill="#a5f3fc" />
        ))
      )}
    </svg>
  );
}

function IconAI() {
  return (
    <svg viewBox="0 0 64 64" className="w-12 h-12">
      <defs>
        <linearGradient id="g2" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#22d3ee" />
        </linearGradient>
      </defs>

      {/* Nodes */}
      {[12, 32, 52].map((x, i) => (
        <circle key={i} cx={x} cy={32} r="4" fill="url(#g2)" />
      ))}

      {/* Connections */}
      <path
        d="M16 32h16M36 32h16M32 16v16M32 32v16"
        stroke="url(#g2)"
        strokeWidth="2"
        strokeLinecap="round"
      />

      {/* Brain outline */}
      <path
        d="M20 20c-6 0-8 8-4 12-4 4 0 12 8 12"
        fill="none"
        stroke="#c7d2fe"
        strokeWidth="2"
      />
      <path
        d="M44 20c6 0 8 8 4 12 4 4 0 12-8 12"
        fill="none"
        stroke="#c7d2fe"
        strokeWidth="2"
      />
    </svg>
  );
}

function IconReproducible() {
  return (
    <svg viewBox="0 0 64 64" className="w-12 h-12">
      <defs>
        <linearGradient id="g3" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#34d399" />
          <stop offset="100%" stopColor="#22d3ee" />
        </linearGradient>
      </defs>

      {/* Circular arrows */}
      <path
        d="M16 32a16 16 0 0 1 28-10"
        fill="none"
        stroke="url(#g3)"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M48 32a16 16 0 0 1-28 10"
        fill="none"
        stroke="url(#g3)"
        strokeWidth="3"
        strokeLinecap="round"
      />

      {/* Lock */}
      <rect x="26" y="30" width="12" height="12" rx="2" fill="#a7f3d0" />
      <path
        d="M28 30v-3a4 4 0 0 1 8 0v3"
        fill="none"
        stroke="#065f46"
        strokeWidth="2"
      />
    </svg>
  );
}

export default function HomeFooter() {
    const [activePillar, setActivePillar] = useState<number | null>(null);
    const PILLARS = [
  {
    title: "Built for Science",
    short:
      "Designed by researchers for genomics, metagenomics, and clinical-grade analysis.",
    long: `CompuBio is engineered with scientific workflows as first-class citizens.

• Genomics & metagenomics pipelines validated against reference standards
• Cohort-aware statistical modeling
• Designed for both exploratory and translational research

The platform reflects how biologists and bioinformaticians actually work.`,
    Icon: IconScience,
  },
  {
    title: "Powered by AI",
    short:
      "Explainable machine learning models surface meaningful biological signals.",
    long: `AI inside CompuBio is never a black box.

• Feature attribution & biological rationale
• Confidence and uncertainty estimation
• AI assists — it does not replace scientific judgment

Every prediction is interpretable and traceable back to biological evidence.`,
    Icon: IconAI,
  },
  {
    title: "Reproducible by Design",
    short:
      "Every analysis is containerized, versioned, and auditable.",
    long: `Reproducibility is enforced at the system level.

• Containerized execution (Docker / Singularity)
• Version-locked pipelines & parameters
• Full execution logs and provenance tracking

Results can always be reproduced — today or years later.`,
    Icon: IconReproducible,
  },
];

  return (
    <footer className="relative bg-[#030011] text-white overflow-hidden">

      {/* ===== Background Effects ===== */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/30 via-purple-900/30 to-fuchsia-900/30 blur-3xl opacity-70 pointer-events-none" />


      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-indigo-600/10 blur-[140px]" />

      {/* ===== Content ===== */}
      <div className="relative max-w-7xl mx-auto px-6 py-28">

        {/* ===== Vision Statement ===== */}
        <div className="text-center max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-extrabold tracking-tight bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            The Future of Bioinformatics
          </h2>

          <p className="mt-6 text-lg md:text-xl text-indigo-100/80 leading-8">
            CompuBio is redefining how biological data becomes knowledge —
            combining AI, reproducible pipelines, and interactive analytics
            into a single, scalable platform.
          </p>
        </div>

       {/* ===== Value Pillars ===== */}
{/* ===== Value Pillars ===== */}
<div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-10 text-center">

  {PILLARS.map((item, i) => {
    const active = activePillar === i;

    return (
      <button
        key={item.title}
        type="button"
        onMouseEnter={() => setActivePillar(i)}
        onClick={() => setActivePillar(i)}
        aria-pressed={active}
        className={`
          relative text-left
          group
          bg-white/5 backdrop-blur-xl
          border rounded-2xl
          p-8 transition-all duration-300
          ${
            active
              ? "border-cyan-400/50 bg-white/10 shadow-[0_0_40px_rgba(34,211,238,0.12)]"
              : "border-white/10 hover:border-cyan-400/30"
          }
        `}
      >
        {active && (
          <div
            aria-hidden
            className="absolute inset-0 rounded-2xl
                       bg-cyan-400/10 blur-2xl pointer-events-none"
          />
        )}

        <div className="relative z-10 text-center">
          <div className="flex justify-center mb-5 group-hover:scale-110 transition">
            <item.Icon />
          </div>

          <h3 className="text-xl font-semibold mb-3 text-white">
            {item.title}
          </h3>

          <p className="text-sm text-indigo-100/70 leading-relaxed">
            {item.short}
          </p>

          <div className="mt-4 text-xs font-semibold text-indigo-200">
            {active ? "Viewing details" : "Hover / tap for details"} →
          </div>
        </div>
      </button>
    );
  })}

</div>

{activePillar !== null && (
  <div
    className="
      mt-16 mx-auto max-w-4xl
      rounded-3xl
      border border-cyan-400/25
      bg-gradient-to-br from-black/70 via-[#050b1f] to-black/80
      p-10
      shadow-[0_0_80px_rgba(34,211,238,0.10)]
    "
  >
    <div className="flex items-center justify-between gap-6">
      <h3 className="text-2xl font-extrabold text-cyan-200">
        {PILLARS[activePillar].title}
      </h3>

      <button
        onClick={() => setActivePillar(null)}
        className="
          text-xs font-semibold
          px-3 py-2 rounded-full
          border border-white/10
          bg-white/5 hover:bg-white/10
          text-indigo-200 transition
        "
      >
        Close
      </button>
    </div>

    <p className="mt-6 text-slate-200 leading-8 whitespace-pre-line">
      {PILLARS[activePillar].long}
    </p>
  </div>
)}

        {/* ===== Call To Action ===== */}
        <div className="mt-24 text-center">
          <h3 className="text-3xl font-bold mb-6">
            Ready to Turn Data into Insight?
          </h3>

          <div className="flex flex-wrap justify-center gap-6">
            <Link
              href="/login"
              className="px-10 py-4 rounded-xl font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 hover:scale-105 transition shadow-lg"
            >
              Get Started
            </Link>

            <Link
              href="/docs"
              className="px-10 py-4 rounded-xl font-semibold border border-white/30 hover:bg-white/10 transition"
            >
              Explore Documentation
            </Link>
          </div>
        </div>

        {/* ===== Trust / Contact ===== */}
        <div className="mt-24 border-t border-white/10 pt-10 flex flex-col md:flex-row items-center justify-between gap-6 text-sm text-indigo-200/60">

          <p>
            © {new Date().getFullYear()} CompuBio Science Solutions
          </p>

          <div className="flex gap-6">
            <span> info@compubio.ca</span>
            <span> (416) 318-8414</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
